import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { useQuery } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { moneyRequestsTableColumns } from "../../../data/FormColumns/Fleet/moneyRequestsTableColumns";
import axios from "axios";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchMoneyRequestsByUsername = async (
  token: string | null | undefined,
  userName: string,
  month?: number,
  year?: number
) => {
  const { data } = await axios.get(`${apiVariable}/getRequestsByUserName`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      userName,
      month,
      year,
    },
  });

  return data;
};

const YourRequestsTable: React.FC = () => {
  const { token } = useAuth();
  const { accounts } = useMsal();
  const userName = accounts[0]?.username;
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["moneyRequests", userName, selectedMonth, selectedYear, showAll],
    queryFn: () =>
      fetchMoneyRequestsByUsername(
        token,
        userName,
        showAll ? undefined : selectedMonth,
        showAll ? undefined : selectedYear
      ),
    enabled: !!userName,
  });

  return (
    <Box className="money-requests-table-container" height={800}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
        }}
      >
        <Grid container spacing={2} sx={{ width: "auto" }}>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                disabled={showAll}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={showAll}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={(e) => {
                    setShowAll(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedMonth(new Date().getMonth() + 1);
                      setSelectedYear(new Date().getFullYear());
                    }
                  }}
                />
              }
              label="Show All Requests"
            />
          </Grid>
        </Grid>
      </Box>
      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {error.message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="yourRequests"
          rows={data || []}
          columns={moneyRequestsTableColumns}
          compactViewConfig={{
            primaryField: "driverName",
            secondaryFields: ["unitNumber", "dateRequested"],
            detailFields: [
              "amount",
              "moneyRequestType",
              "paymentMethod",
              "nameRequestedBy",
              "referenceId",
              "issuedFor",
              "description",
            ],
            actionFields: [
              "downloadInvoice",
              "downloadReceipt",
              "update",
              "delete",
            ],
            statusField: "status",
          }}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default YourRequestsTable;
